import React from 'react';

import { VerticalSpacing, VerticalSpacingType } from 'ui/component';
import { StyleUtil } from 'ui/util';
import classNames from './EABDJourneyFooter.module.scss';

export interface JourneyFooterProps {
  readonly leftTitle: string;
  readonly leftSubtitle: string;
  readonly rightTitle: string;
  readonly rightSubtitle: string;
  readonly numberOfItems: number;
  readonly numberOfItemsPerPage: number;
  readonly currentItemIndex: number;
  readonly style?: React.CSSProperties;
}

export const JourneyFooter = React.memo<JourneyFooterProps>((props) => {
  const { leftTitle, leftSubtitle, rightTitle, rightSubtitle, numberOfItems, numberOfItemsPerPage, currentItemIndex, style } = props;

  const ShouldHiglight = React.useMemo(() => {
    let numberOfPages = 0;

    if (numberOfItemsPerPage === 1) {
      numberOfPages = Math.ceil(numberOfItems / numberOfItemsPerPage);
    } else {
      numberOfPages = numberOfItems + 1 - numberOfItemsPerPage;
    }

    const currentPage = currentItemIndex + 1;

    return currentPage === numberOfPages;
  }, [numberOfItems, numberOfItemsPerPage, currentItemIndex]);

  const RenderTextContainerCallback = React.useCallback((title: string, subtitle: string, className: string) => {
    const textContainerStyles: string[] = [classNames.textContainer, className];
    const textContainerClassName = StyleUtil.mergeClassNames(textContainerStyles);

    return (
      <div className={textContainerClassName}>
        <p className={classNames.title}>{title}</p>
        <VerticalSpacing type={VerticalSpacingType.X05} />
        <p className={classNames.subtitle}>{subtitle}</p>
      </div>
    );
  }, []);

  const ProgressLine = React.useMemo(() => {
    let numberOfPages = 0;

    if (numberOfItemsPerPage === 1) {
      numberOfPages = Math.ceil(numberOfItems / numberOfItemsPerPage);
    } else {
      numberOfPages = numberOfItems + 1 - numberOfItemsPerPage;
    }

    const fullWidthPercent = 100;
    const widthPerPage = fullWidthPercent / numberOfPages;
    const currentPage = currentItemIndex + 1;

    const actualWidthPercent = widthPerPage * currentPage;

    const progressLineClassNames: string[] = [classNames.progressLine];

    const progressLineStyle: React.CSSProperties = {
      width: actualWidthPercent < 100 ? `${actualWidthPercent}%` : `calc(${actualWidthPercent}% - 1px)`,
    };

    return <div className={StyleUtil.mergeClassNames(progressLineClassNames)} style={progressLineStyle} />;
  }, [numberOfItems, numberOfItemsPerPage, currentItemIndex]);

  const Line = React.useMemo(() => {
    const lineClassNames: string[] = [classNames.line];

    if (ShouldHiglight) {
      lineClassNames.push(classNames.highlight);
    }

    return <div className={StyleUtil.mergeClassNames(lineClassNames)}>{ProgressLine}</div>;
  }, [ShouldHiglight, ProgressLine]);

  const Arrow = React.useMemo(() => {
    const arrowClassNames: string[] = [classNames.arrow];

    if (ShouldHiglight) {
      arrowClassNames.push(classNames.highlight);
    }

    return <div className={StyleUtil.mergeClassNames(arrowClassNames)} />;
  }, [ShouldHiglight]);

  return (
    <div style={style} className={classNames.container}>
      {RenderTextContainerCallback(leftTitle, leftSubtitle, classNames.left)}
      <div className={classNames.arrowContainer}>
        {Line}
        {Arrow}
      </div>
      {RenderTextContainerCallback(rightTitle, rightSubtitle, classNames.right)}
    </div>
  );
});
